import React, { useState } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { CustomButton } from './CustomButton';
import { graphqlRequest, testGraphQLConnection } from '../../utils/graphqlRequest';
import { programsQuery, exercisesQuery, constrainedProgramsQuery } from '../../constants/queries';
import { useToastContext } from './ToastManager';

export const GraphQLTester: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<Record<string, string>>({});
  const { showSuccess, showError, showInfo } = useToastContext();

  const testConnection = async () => {
    setIsLoading(true);
    try {
      const isConnected = await testGraphQLConnection();
      if (isConnected) {
        showSuccess('Connection Test', 'GraphQL endpoint is accessible');
        setResults(prev => ({ ...prev, connection: '✅ Connected' }));
      } else {
        showError('Connection Test', 'Failed to connect to GraphQL endpoint');
        setResults(prev => ({ ...prev, connection: '❌ Failed' }));
      }
    } catch (error) {
      showError('Connection Test', 'Error testing connection');
      setResults(prev => ({ ...prev, connection: '❌ Error' }));
    }
    setIsLoading(false);
  };

  const testProgramsQuery = async () => {
    setIsLoading(true);
    try {
      const data = await graphqlRequest(programsQuery, undefined, showError);
      showSuccess('Programs Query', `Retrieved ${data.programs?.length || 0} programs`);
      setResults(prev => ({ ...prev, programs: `✅ ${data.programs?.length || 0} programs` }));
    } catch (error) {
      showError('Programs Query', 'Failed to fetch programs');
      setResults(prev => ({ ...prev, programs: '❌ Failed' }));
    }
    setIsLoading(false);
  };

  const testExercisesQuery = async () => {
    setIsLoading(true);
    try {
      const data = await graphqlRequest(exercisesQuery, undefined, showError);
      showSuccess('Exercises Query', `Retrieved ${data.exercises?.length || 0} exercises`);
      setResults(prev => ({ ...prev, exercises: `✅ ${data.exercises?.length || 0} exercises` }));
    } catch (error) {
      showError('Exercises Query', 'Failed to fetch exercises');
      setResults(prev => ({ ...prev, exercises: '❌ Failed' }));
    }
    setIsLoading(false);
  };

  const testConstrainedProgramsQuery = async () => {
    setIsLoading(true);
    try {
      const data = await graphqlRequest(constrainedProgramsQuery, undefined, showError);
      showSuccess('Constrained Programs Query', `Retrieved ${data.programsConstrained?.length || 0} constrained programs`);
      setResults(prev => ({ ...prev, constrainedPrograms: `✅ ${data.programsConstrained?.length || 0} constrained programs` }));
    } catch (error) {
      showError('Constrained Programs Query', 'Failed to fetch constrained programs');
      setResults(prev => ({ ...prev, constrainedPrograms: '❌ Failed' }));
    }
    setIsLoading(false);
  };

  return (
    <Box p={4} border="1px solid #e2e8f0" borderRadius="8px" bg="white">
      <Text fontSize="lg" fontWeight="bold" mb={4}>GraphQL Connection Tester</Text>
      
      <Flex gap={2} mb={4} flexWrap="wrap">
        <CustomButton 
          onClick={testConnection} 
          loading={isLoading}
          size="sm"
          visual="solid"
        >
          Test Connection
        </CustomButton>
        <CustomButton 
          onClick={testProgramsQuery} 
          loading={isLoading}
          size="sm"
          visual="solid"
        >
          Test Programs
        </CustomButton>
        <CustomButton 
          onClick={testExercisesQuery} 
          loading={isLoading}
          size="sm"
          visual="solid"
        >
          Test Exercises
        </CustomButton>
        <CustomButton 
          onClick={testConstrainedProgramsQuery} 
          loading={isLoading}
          size="sm"
          visual="solid"
        >
          Test Constrained Programs
        </CustomButton>
      </Flex>

      <Box>
        <Text fontWeight="medium" mb={2}>Results:</Text>
        {Object.entries(results).map(([key, value]) => (
          <Text key={key} fontSize="sm" mb={1}>
            {key}: {value}
          </Text>
        ))}
      </Box>
    </Box>
  );
}; 